import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { leadFormEventName } from "@/lib/lead-form";
import { useCallback, useEffect, useRef, useState } from "react";
import type intlTelInput from "intl-tel-input";
import { toast } from "@/hooks/use-toast";
import { useExperimentVariant } from "@/context/ExperimentContext";

type IntlTelInputFactory = typeof intlTelInput;
type IntlTelInstance = ReturnType<IntlTelInputFactory>;

declare global {
  interface Window {
    intlTelInputUtils?: {
      numberFormat: {
        E164: number;
        INTERNATIONAL: number;
        NATIONAL: number;
        RFC3966: number;
      };
    };
    dataLayer?: unknown[];
  }
}

const formatBrazilianNationalNumber = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) {
    return "";
  }
  if (digits.length <= 2) {
    return digits;
  }

  const ddd = digits.slice(0, 2);
  const number = digits.slice(2);

  if (number.length <= 4) {
    return `(${ddd}) ${number}`;
  }

  if (number.length <= 8) {
    return `(${ddd}) ${number.slice(0, 4)}-${number.slice(4)}`;
  }

  return `(${ddd}) ${number.slice(0, 5)}-${number.slice(5)}`;
};

const isValidBrazilianNumber = (value: string) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 11) {
    return false;
  }

  const ddd = Number(digits.slice(0, 2));
  if (ddd < 11 || ddd > 99) {
    return false;
  }

  return true;
};

const sanitizeText = (value: string) => {
  return value.replace(/[<>]/g, "").trim();
};

const getCookieValue = (name: string) => {
  if (typeof document === "undefined") {
    return undefined;
  }

  const cookies = document.cookie?.split(";") ?? [];
  const target = cookies
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));

  if (!target) {
    return undefined;
  }

  const [, value] = target.split("=", 2);
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const MIN_FORM_OPEN_TIME_MS = 2500;
const RESUBMIT_COOLDOWN_MS = 15000;

type HeroProps = {
  desktopBackground?: string;
  mobileBackground?: string;
};

const Hero = ({
  desktopBackground = "/img/hero-a-desktop.webp",
  mobileBackground = "/img/hero-a-mobile.webp",
}: HeroProps) => {
  const experimentVariant = useExperimentVariant();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    telefone: "",
  });
  const [phoneError, setPhoneError] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [hideFloatingCard, setHideFloatingCard] = useState(false);
  const [honeypotValue, setHoneypotValue] = useState("");
  const [formOpenTimestamp, setFormOpenTimestamp] = useState<number | null>(
    null,
  );
  const lastSubmissionTimestampRef = useRef<number | null>(null);
  const submissionLockRef = useRef(false);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const [isPhoneInputReady, setIsPhoneInputReady] = useState(false);
  const [isIntlTelInputReady, setIsIntlTelInputReady] = useState(false);
  const handlePhoneInputRef = useCallback((node: HTMLInputElement | null) => {
    phoneInputRef.current = node;
    setIsPhoneInputReady(Boolean(node));
  }, []);
  const intlTelInputFactoryRef = useRef<IntlTelInputFactory | null>(null);
  const intlTelInputUtilsUrlRef = useRef<string | null>(null);
  const itiRef = useRef<IntlTelInstance | null>(null);
  const getDialCodeFromDom = useCallback(() => {
    const dialCodeElement = phoneInputRef.current
      ?.closest(".iti")
      ?.querySelector<HTMLElement>(".iti__selected-dial-code");
    const dialCodeText = dialCodeElement?.textContent ?? "";
    return dialCodeText.replace(/[^\d]/g, "");
  }, []);
  const syncPhoneFromInput = useCallback(
    (markTouched = false) => {
      const inputEl = phoneInputRef.current;
      const itiInstance = itiRef.current;
      if (!inputEl || !itiInstance) {
        return;
      }

      if (markTouched) {
        setPhoneTouched(true);
      }

      const selectedCountry = itiInstance.getSelectedCountryData();
      const domDialCode = getDialCodeFromDom();
      const isBrazil =
        domDialCode === "55" ||
        selectedCountry?.iso2 === "br" ||
        selectedCountry?.dialCode === "55";

      if (isBrazil) {
        inputEl.value = formatBrazilianNationalNumber(inputEl.value);
      }

      const utils =
        typeof window !== "undefined" ? window.intlTelInputUtils : undefined;
      let storedValue = inputEl.value;

      if (utils) {
        storedValue =
          itiInstance.getNumber(utils.numberFormat.E164) ||
          itiInstance.getNumber(utils.numberFormat.INTERNATIONAL) ||
          inputEl.value;
      } else {
        storedValue = itiInstance.getNumber() || inputEl.value;
      }

      setFormData((prev) => ({
        ...prev,
        telefone: storedValue ?? "",
      }));

      const hasValue = inputEl.value.trim().length > 0;
      const shouldValidate = (phoneTouched || markTouched) && isBrazil;

      if (!hasValue) {
        if (phoneTouched || markTouched) {
          setPhoneError("Informe o telefone");
        } else {
          setPhoneError("");
        }
        return;
      }

      if (!isBrazil) {
        setPhoneError("");
        return;
      }

      if (!shouldValidate) {
        setPhoneError("");
        return;
      }

      const digits = inputEl.value.replace(/\D/g, "");
      if (!isValidBrazilianNumber(inputEl.value)) {
        setPhoneError("Informe DDD + número completo");
        return;
      }

      setPhoneError("");
    },
    [getDialCodeFromDom, phoneTouched],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleExternalOpen = () => {
      setIsFormOpen(true);
    };

    window.addEventListener(leadFormEventName, handleExternalOpen);
    return () => {
      window.removeEventListener(leadFormEventName, handleExternalOpen);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const buffer = 200;
      setHideFloatingCard(scrollPosition + buffer >= documentHeight);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!isFormOpen) {
      return;
    }

    if (intlTelInputFactoryRef.current) {
      if (!isIntlTelInputReady) {
        setIsIntlTelInputReady(true);
      }
      return;
    }

    let cancelled = false;

    const loadIntlTelInput = async () => {
      try {
        const [intlModule, utilsModule] = await Promise.all([
          import("intl-tel-input"),
          import("intl-tel-input/build/js/utils.js?url"),
          import("intl-tel-input/build/css/intlTelInput.css"),
        ]);

        if (cancelled) {
          return;
        }

        const intlFactory = (intlModule.default ??
          intlModule) as IntlTelInputFactory;
        intlTelInputFactoryRef.current = intlFactory;
        intlTelInputUtilsUrlRef.current =
          typeof utilsModule.default === "string" ? utilsModule.default : null;
        setIsIntlTelInputReady(true);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[LeadForm] Falha ao carregar intl-tel-input", error);
      }
    };

    loadIntlTelInput();

    return () => {
      cancelled = true;
    };
  }, [isFormOpen, isIntlTelInputReady]);

  useEffect(() => {
    if (
      !isFormOpen ||
      !isPhoneInputReady ||
      !phoneInputRef.current ||
      !isIntlTelInputReady ||
      !intlTelInputFactoryRef.current
    ) {
      return;
    }

    const inputElement = phoneInputRef.current;
    const intlTelInputFactory = intlTelInputFactoryRef.current;
    const utilsScriptUrl = intlTelInputUtilsUrlRef.current ?? undefined;
    itiRef.current = intlTelInputFactory(inputElement, {
      initialCountry: "br",
      nationalMode: true,
      autoPlaceholder: "polite",
      formatOnDisplay: true,
      separateDialCode: true,
      utilsScript: utilsScriptUrl,
    });

    const handleInput = () => {
      syncPhoneFromInput(false);
    };

    const handleCountryChange = () => {
      syncPhoneFromInput(false);
    };

    const handleBlur = () => {
      syncPhoneFromInput(true);
    };

    inputElement.addEventListener("input", handleInput);
    inputElement.addEventListener("countrychange", handleCountryChange);
    inputElement.addEventListener("blur", handleBlur);

    return () => {
      inputElement.removeEventListener("countrychange", handleCountryChange);
      inputElement.removeEventListener("input", handleInput);
      inputElement.removeEventListener("blur", handleBlur);
      itiRef.current?.destroy();
    };
  }, [
    isFormOpen,
    isPhoneInputReady,
    isIntlTelInputReady,
    syncPhoneFromInput,
  ]);

  useEffect(() => {
    if (!isFormOpen) {
      return;
    }

    setHoneypotValue("");
    setFormOpenTimestamp(Date.now());
  }, [isFormOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const now = Date.now();

    if (honeypotValue.trim().length > 0) {
      toast({
        title: "Não foi possível enviar",
        description:
          "Detectamos um preenchimento automático. Recarregue a página e tente novamente.",
        variant: "destructive",
      });
      return;
    }

    if (
      formOpenTimestamp &&
      now - formOpenTimestamp < MIN_FORM_OPEN_TIME_MS
    ) {
      toast({
        title: "Envio muito rápido",
        description:
          "Abra o formulário, revise as informações e tente novamente em instantes.",
        variant: "destructive",
      });
      return;
    }

    if (
      lastSubmissionTimestampRef.current &&
      now - lastSubmissionTimestampRef.current < RESUBMIT_COOLDOWN_MS
    ) {
      toast({
        title: "Aguarde antes de enviar novamente",
        description: "Estamos processando o envio anterior.",
        variant: "destructive",
      });
      return;
    }

    if (submissionLockRef.current) {
      toast({
        title: "Aguarde antes de enviar novamente",
        description: "Estamos processando o envio anterior.",
        variant: "destructive",
      });
      return;
    }

    setPhoneTouched(true);
    syncPhoneFromInput(true);
    const itiInstance = itiRef.current;
    const inputValue = phoneInputRef.current?.value.trim() ?? "";
    const selectedCountry = itiInstance?.getSelectedCountryData();
    const domDialCode = getDialCodeFromDom();
    const dialCode =
      domDialCode ||
      selectedCountry?.dialCode ||
      "";
    const isBrazil =
      dialCode === "55" ||
      selectedCountry?.iso2 === "br";
    
    if (!formData.fullName || !formData.email || !inputValue) {
      // eslint-disable-next-line no-console
      console.warn("[LeadForm] Campos obrigatórios ausentes", {
        fullName: Boolean(formData.fullName),
        email: Boolean(formData.email),
        phone: Boolean(inputValue),
      });
      setPhoneError((prev) => (inputValue ? prev : "Informe o telefone"));
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos do formulário.",
        variant: "destructive",
      });
      return;
    }

    if (isBrazil && !isValidBrazilianNumber(inputValue)) {
      // eslint-disable-next-line no-console
      console.warn("[LeadForm] Telefone BR inválido", {
        inputValue,
        digits: inputValue.replace(/\D/g, ""),
      });
      setPhoneError("Telefone inválido");
      toast({
        title: "Telefone inválido",
        description: "Verifique o número informado e tente novamente.",
        variant: "destructive",
      });
      return;
    }

    const utils =
      typeof window !== "undefined" ? window.intlTelInputUtils : undefined;
    const rawPhoneValue =
      (utils && itiInstance?.getNumber(utils.numberFormat.E164)) ||
      itiInstance?.getNumber() ||
      formData.telefone ||
      inputValue ||
      "";
    const digitsOnlyPhone = rawPhoneValue.replace(/\D/g, "");
    const phoneDigitsWithDialCode =
      dialCode && digitsOnlyPhone && !digitsOnlyPhone.startsWith(dialCode)
        ? `${dialCode}${digitsOnlyPhone}`
        : digitsOnlyPhone;
    const phoneWithPlus =
      phoneDigitsWithDialCode.length > 0
        ? `+${phoneDigitsWithDialCode}`
        : "";
    const sanitizedName = sanitizeText(formData.fullName);
    const sanitizedEmail = sanitizeText(formData.email).toLowerCase();
    const source =
      typeof window !== "undefined" ? window.location.href : undefined;
    const referer =
      typeof document !== "undefined" ? document.referrer : undefined;
    const utmParams =
      typeof window !== "undefined"
        ? Object.fromEntries(new URLSearchParams(window.location.search))
        : {};
    const facebookFbc =
      typeof document !== "undefined"
        ? getCookieValue("_fbc") || getCookieValue("fbc")
        : undefined;
    const facebookFbp =
      typeof document !== "undefined"
        ? getCookieValue("_fbp") || getCookieValue("fbp")
        : undefined;
    const stapeVisitorId =
      typeof document !== "undefined"
        ? getCookieValue("stape_user_id")
        : undefined;

    const defaultMediaId =
      Number(import.meta.env.VITE_DEFAULT_MEDIA_ID) || 63;

    const phoneValue = phoneWithPlus || phoneDigitsWithDialCode;

    const leadRequestPayload = {
      name: sanitizedName,
      fullName: sanitizedName,
      email: sanitizedEmail,
      telefone: phoneValue,
      phoneDigitsWithDialCode,
      phoneWithPlus,
      dialCode,
      utmParams,
      source,
      referer,
      mediaId: defaultMediaId,
      variant: experimentVariant ?? undefined,
      fbc: facebookFbc,
      fbp: facebookFbp,
      visitorId: stapeVisitorId,
    };

    try {
      submissionLockRef.current = true;
      lastSubmissionTimestampRef.current = now;
      setIsSubmitting(true);
      // eslint-disable-next-line no-console
      console.info("[LeadForm] Sending payload metadata", {
        endpoint: "/api/leads/external",
        fields: Object.keys(leadRequestPayload),
      });
      const response = await fetch("/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(leadRequestPayload),
      });
      const responseText = await response.text();
      // eslint-disable-next-line no-console
      console.info("[LeadForm] Response received", {
        status: response.status,
        body: responseText,
      });
      if (!response.ok) {
        throw new Error(
          `Falha ao enviar dados (${response.status}): ${responseText.slice(0, 500)}`,
        );
      }

      toast({
        title: "Solicitação enviada!",
        description: "Em breve entraremos em contato com você.",
      });

      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "formEnviadoValido",
          fullName: sanitizedName,
          email: sanitizedEmail,
          phone: phoneValue,
          utm: utmParams,
          variant: experimentVariant ?? undefined,
          source,
        });
      }

      itiInstance.setNumber("");
      itiInstance.setCountry("br");
      if (phoneInputRef.current) {
        phoneInputRef.current.value = "";
      }
      setFormData({ fullName: "", email: "", telefone: "" });
      setPhoneError("");
      setPhoneTouched(false);
      setFormOpenTimestamp(Date.now());
      setHoneypotValue("");
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[LeadForm] Erro ao enviar", error);
      lastSubmissionTimestampRef.current = null;
      toast({
        title: "Erro ao enviar",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section
        id="inicio"
        className="relative flex min-h-screen scroll-mt-24 items-center py-0 lg:py-0 pt-[26rem] pb-10 lg:pt-0 lg:pb-0 bg-primary md:bg-transparent"
      >
        {/* Background with overlay */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 overflow-hidden">
            <picture className="absolute inset-0 block" aria-hidden="true">
              <source media="(min-width: 768px)" srcSet={desktopBackground} />
              <img
                src={mobileBackground}
                alt=""
                role="presentation"
                fetchPriority="high"
                loading="eager"
                decoding="async"
                width={1920}
                height={1080}
                className="h-full w-full object-contain object-top md:object-cover md:object-center hero-mobile-mask"
                sizes="100vw"
              />
            </picture>
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-30" />
          </div>
        </div>

        <div className="container mx-auto px-4 relative z-10 mt-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 lg:mb-6 leading-tight">
              Homologação de Divórcio no Brasil
            </h1>

            <p className="text-lg md:text-xl lg:text-2xl mb-6 lg:mb-8 text-white/90 leading-relaxed">
              Você se divorciou no exterior? É hora de validá-lo no Brasil! Para um processo seguro e transparente em cada etapa, conte com acompanhamento de um escritório especializado.
            </p>

            <div className="space-y-3 mb-6 lg:mb-8">
              <div className="flex items-start gap-2 lg:gap-3">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-accent mt-1.5 lg:mt-2 flex-shrink-0" />
                <p className="text-base lg:text-lg text-white/90 max-w-xl">
                  <strong className="text-white">Averbação de Divórcio Estrangeiro, Homologação de Sentença de Divórcio, Regularização de Estado Civil.</strong>
                </p>
              </div>
              <div className="flex items-start gap-2 lg:gap-3">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-accent mt-1.5 lg:mt-2 flex-shrink-0" />
                <p className="text-base lg:text-lg text-white/90">
                  <strong className="text-white">Atendimento online ou presencial.</strong>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
              <Button
                size="lg"
                variant="gold"
                className="font-semibold w-full sm:w-auto"
                onClick={() => setIsFormOpen(true)}
              >
                Falar com advogado especialista
              </Button>
            </div>

          </div>
        </div>
      </section>

      <div
        className={cn(
          "fixed bottom-4 right-4 z-30 flex items-center gap-3 transition-opacity duration-300",
          hideFloatingCard ? "opacity-0 pointer-events-none" : "opacity-100",
        )}
      >
        <button
          type="button"
          onClick={() => setIsFormOpen(true)}
          className="bg-white rounded-2xl px-3 py-2 text-left shadow-xl border border-black/5 max-w-64 hover:shadow-2xl transition-shadow text-sm md:px-4 md:py-3 md:text-base"
        >
          <p className="font-semibold text-foreground leading-tight">Olá, tudo bem?</p>
          <p className="text-xs md:text-sm text-muted-foreground leading-snug">
            Clique aqui e agende uma consulta com o especialista.
          </p>
        </button>
        <div className="relative h-20 w-20">
          <span className="whatsapp-wave" aria-hidden="true" />
          <span className="whatsapp-wave whatsapp-wave--delay" aria-hidden="true" />
          <Button
            size="icon"
            variant="ghost"
            className="relative h-20 w-20 p-0 overflow-hidden hover:scale-110 transition-transform bg-transparent shadow-none border-none hover:bg-transparent focus-visible:ring-0"
            onClick={() => setIsFormOpen(true)}
            aria-label="Abrir formulário de avaliação"
          >
            <img
              src="/img/especialista.webp"
              alt="Especialista"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          </Button>
        </div>
      </div>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Análise jurídica personalizada do seu caso</DialogTitle>
            <DialogDescription>
              Preencha o formulário para uma avaliação inicial conduzida por nossos advogados especialistas.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fullName" className="text-foreground font-medium text-sm lg:text-base">Nome Completo *</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Seu nome"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                className="mt-1.5 h-11 lg:h-12 text-base"
                required
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-foreground font-medium text-sm lg:text-base">E-mail *</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1.5 h-11 lg:h-12 text-base"
                required
              />
            </div>

            <div>
              <Label htmlFor="telefone" className="text-foreground font-medium text-sm lg:text-base">Telefone/WhatsApp *</Label>
              <Input
                ref={handlePhoneInputRef}
                id="telefone"
                name="telefone"
                type="tel"
                placeholder="Ex: 11 99999-8888"
                className={cn(
                  "mt-1.5 h-11 lg:h-12 text-base",
                  phoneError && "border-destructive focus-visible:ring-destructive",
                )}
                aria-invalid={Boolean(phoneError)}
                aria-describedby={phoneError ? "telefone-error" : undefined}
                required
              />
              {phoneError && (
                <p id="telefone-error" className="mt-1 text-sm text-destructive">
                  {phoneError}
                </p>
              )}
            </div>

            <div className="hidden" aria-hidden="true">
              <Label htmlFor="empresa" className="text-foreground font-medium text-sm lg:text-base">
                Empresa
              </Label>
              <Input
                id="empresa"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypotValue}
                onChange={(e) => setHoneypotValue(e.target.value)}
                className="mt-1.5 h-11 lg:h-12 text-base"
              />
            </div>

            <Button
              type="submit"
              variant="gold"
              size="lg"
              className="w-full font-semibold text-base"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Solicitar avaliação"}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              Seus dados estão protegidos e serão utilizados apenas para contato
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Hero;
